import React, { useEffect, useState, useRef, memo } from 'react';
import { loadGoogleMapsScript } from '../services/GoogleMap';

// Helper function to extract emirate from address components
const extractEmirate = (addressComponents) => {
  if (!addressComponents) return '';
  
  const emirates = ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Umm Al Quwain', 'Ras Al Khaimah', 'Fujairah'];
  
  // Look for administrative_area_level_1 (emirate level)
  const emirateComponent = addressComponents.find(
    component => component.types.includes('administrative_area_level_1')
  );
  
  if (emirateComponent) {
    const emirateName = emirateComponent.long_name || emirateComponent.short_name;
    // Check if it matches any of our emirates
    for (const emirate of emirates) {
      if (emirateName.includes(emirate) || emirate.includes(emirateName)) {
        return emirate;
      }
    }
    // If not exact match, try to find partial match
    const matchedEmirate = emirates.find(e => emirateName.toLowerCase().includes(e.toLowerCase()) || e.toLowerCase().includes(emirateName.toLowerCase()));
    if (matchedEmirate) return matchedEmirate;
  }
  
  return '';
};

// Create custom black marker icon (pin shape) with white stroke
const createBlackMarkerIcon = (googleMaps) => {
  // Custom pin path
  const pinPath = 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z';
  return {
    path: pinPath,
    fillColor: '#4F97D8',
    fillOpacity: 1,
    strokeColor: '#FFFFFF',
    strokeWeight: 2,
    scale: 2,
    anchor: new googleMaps.Point(12, 35),
  };
};

const GoogleMapSelector = memo(({ locationNew, onLocationChanged }) => {
  const [location, setLocation] = useState(locationNew || { text: '', lat: null, lng: null });
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const autocompleteRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const autocompleteInstanceRef = useRef(null);
  const initializedRef = useRef(false);

  // Update location when locationNew prop changes
  useEffect(() => {
    if (locationNew && (locationNew.text !== location.text || locationNew.lat !== location.lat || locationNew.lng !== location.lng)) {
      setLocation(locationNew);
      
      // Update input field if it exists
      if (autocompleteRef.current) {
        autocompleteRef.current.value = locationNew.text || '';
      }
      
      // Update map and marker if they exist
      if (mapInstanceRef.current && locationNew.lat && locationNew.lng) {
        const center = { lat: parseFloat(locationNew.lat), lng: parseFloat(locationNew.lng) };
        mapInstanceRef.current.setCenter(center);
        
        if (markerRef.current) {
          markerRef.current.setPosition(center);
        } else {
          // Create new marker if it doesn't exist
          const googleMaps = window.google?.maps;
          if (googleMaps) {
            markerRef.current = new googleMaps.Marker({
              position: center,
              map: mapInstanceRef.current,
              title: locationNew.text || 'Selected Location',
              draggable: true,
              icon: createBlackMarkerIcon(googleMaps)
            });
          }
        }
      }
    }
  }, [locationNew]);

  useEffect(() => {
    const initializeMapAndAutocomplete = async () => {
      const map_key = localStorage.getItem('map_key');

      if (!map_key) {
        console.error("Google Maps API key is missing");
        return;
      }

      try {
        const googleMaps = await loadGoogleMapsScript(map_key);

        // Initialize map
        const map = new googleMaps.Map(mapRef.current, {
          center: location.lat && location.lng 
            ? { lat: parseFloat(location.lat), lng: parseFloat(location.lng) }
            : { lat: 25.2048, lng: 55.2708 }, // Default to Dubai
          zoom: 13,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: true,
        });
        
        mapInstanceRef.current = map;

        // Initialize autocomplete - restricted to UAE only
        const autocomplete = new googleMaps.places.Autocomplete(
          autocompleteRef.current,
          { 
            types: ['geocode'],
            componentRestrictions: { country: 'ae' }
          }
        );
        
        autocompleteInstanceRef.current = autocomplete;
        
        // Set initial value if location exists
        if (location.text && autocompleteRef.current) {
          autocompleteRef.current.value = location.text;
        }

        // Add marker if location exists
        if (location.lat && location.lng) {
          markerRef.current = new googleMaps.Marker({
            position: { lat: parseFloat(location.lat), lng: parseFloat(location.lng) },
            map: map,
            title: location.text || 'Selected Location',
            draggable: true,
            icon: createBlackMarkerIcon(googleMaps)
          });
        }

        // Handle autocomplete selection
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (place.geometry) {
            const emirate = extractEmirate(place.address_components);
            const newLocation = {
              text: place.formatted_address,
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
              emirate: emirate,
            };
            setLocation(newLocation);
            onLocationChanged(newLocation);

            // Update map center and marker
            map.setCenter({ lat: newLocation.lat, lng: newLocation.lng });
            
            if (markerRef.current) {
              markerRef.current.setPosition({ lat: newLocation.lat, lng: newLocation.lng });
            } else {
              markerRef.current = new googleMaps.Marker({
                position: { lat: newLocation.lat, lng: newLocation.lng },
                map: map,
                title: newLocation.text,
                draggable: true,
                icon: createBlackMarkerIcon(googleMaps)
              });
            }
          }
        });

        // Handle map clicks
        map.addListener('click', (event) => {
          const lat = event.latLng.lat();
          const lng = event.latLng.lng();
          
          // Reverse geocoding to get address - restricted to UAE
          const geocoder = new googleMaps.Geocoder();
          geocoder.geocode({ 
            location: { lat, lng }
          }, (results, status) => {
            if (status === 'OK' && results[0]) {
              // Filter to only UAE results
              const uaeResult = results.find(result => {
                const countryComponent = result.address_components.find(
                  component => component.types.includes('country')
                );
                return countryComponent && countryComponent.short_name === 'AE';
              }) || results[0]; // Fallback to first result if no UAE result found
              
              const emirate = extractEmirate(uaeResult.address_components);
              const newLocation = {
                text: uaeResult.formatted_address,
                lat: lat,
                lng: lng,
                emirate: emirate,
              };
              setLocation(newLocation);
              onLocationChanged(newLocation);

              // Update autocomplete input
              autocompleteRef.current.value = newLocation.text;

              // Update marker
              if (markerRef.current) {
                markerRef.current.setPosition({ lat, lng });
              } else {
                markerRef.current = new googleMaps.Marker({
                  position: { lat, lng },
                  map: map,
                  title: newLocation.text,
                  draggable: true,
                  icon: createBlackMarkerIcon(googleMaps)
                });
              }
            }
          });
        });

        // Handle marker drag
        if (markerRef.current) {
          markerRef.current.addListener('dragend', (event) => {
            const lat = event.latLng.lat();
            const lng = event.latLng.lng();
            
          // Reverse geocoding to get address - restricted to UAE
          const geocoder = new googleMaps.Geocoder();
          geocoder.geocode({ 
            location: { lat, lng }
          }, (results, status) => {
            if (status === 'OK' && results[0]) {
              // Filter to only UAE results
              const uaeResult = results.find(result => {
                const countryComponent = result.address_components.find(
                  component => component.types.includes('country')
                );
                return countryComponent && countryComponent.short_name === 'AE';
              }) || results[0]; // Fallback to first result if no UAE result found
              
              const emirate = extractEmirate(uaeResult.address_components);
              const newLocation = {
                text: uaeResult.formatted_address,
                lat: lat,
                lng: lng,
                emirate: emirate,
              };
              setLocation(newLocation);
              onLocationChanged(newLocation);

              // Update autocomplete input
              autocompleteRef.current.value = newLocation.text;
            }
          });
          });
        }

      } catch (error) {
        console.error("Error loading Google Maps script:", error);
      }
    };

    if (initializedRef.current) return;
    if (mapRef.current && autocompleteRef.current) {
      initializeMapAndAutocomplete();
      initializedRef.current = true;
    }
  }, [location.text, location.lat, location.lng, onLocationChanged]);

  const handleInputChange = (e) => {
    const newText = e.target.value;
    setLocation((prev) => ({
      ...prev,
      text: newText,
    }));
  };

  // Add style for Google autocomplete dropdown
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .pac-container {
        z-index: 10000 !important;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        border: 1px solid #e0e0e0;
        margin-top: 4px;
      }
      .pac-item {
        padding: 12px;
        cursor: pointer;
        border-bottom: 1px solid #f0f0f0;
      }
      .pac-item:last-child {
        border-bottom: none;
      }
      .pac-item:hover {
        background-color: #f8f9fa;
      }
      .pac-item-selected {
        background-color: #e8f4f8;
      }
      .pac-icon {
        margin-right: 8px;
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  return (
    <div className="google-map-selector">
      {/* Autocomplete Input */}
      <div className="mb-3">
        <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>
          Search Location
        </label>
        <input
          ref={autocompleteRef}
          type="text"
          value={location?.text || ''}
          onChange={handleInputChange}
          className="form-control form-control-solid"
          placeholder="Search for a location or click on the map"
          style={{
            width: '100%',
            padding: '12px 18px',
            border: '1px solid #e1e5e9',
            borderRadius: '12px',
            fontSize: '16px'
          }}
        />
        <small className="text-muted" style={{ display: 'block', marginTop: '4px' }}>
          Type to search or click on the map to select a location
        </small>
      </div>
      
      {/* Map Display */}
      <div 
        ref={mapRef} 
        style={{ 
          width: '100%', 
          height: '400px',
          borderRadius: '12px',
          border: '1px solid #e1e5e9',
          marginTop: '16px'
        }}
      />
      
      {/* Location Details */}
      {location.lat && location.lng && (
        <div className="mt-3 p-3" style={{
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e0e0e0'
        }}>
          <div className="d-flex align-items-center mb-2">
            <i className="bi bi-geo-alt-fill me-2 text-base" ></i>
            <strong>Selected Location:</strong>
          </div>
          <p className="mb-1" style={{ marginLeft: '24px' }}>{location.text}</p>
          <small className="text-muted" style={{ marginLeft: '24px' }}>
            Coordinates: {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
          </small>
        </div>
      )}
    </div>
  );
});

GoogleMapSelector.displayName = 'GoogleMapSelector';

export default GoogleMapSelector;
